/** Session + dismiss persistence for post-registration operator setup checklist. */

/** @deprecated Legacy global dismiss key — cleared on logout. */
export const SETUP_CHECKLIST_DISMISS_KEY = "provider_setup_checklist_dismissed";

const SESSION_KEY_PREFIX = "provider_setup_checklist_session";
const DISMISS_KEY_PREFIX = "provider_setup_checklist_dismissed";
/** @deprecated Unscoped session key from v1 — removed on read/migrate. */
const LEGACY_SESSION_KEY = "provider_setup_checklist_session";

export type SetupChecklistSession = {
  visible: true;
  profileCompletionRequired: boolean;
  scopeId: string;
};

export function normalizeChecklistScopeId(
  scopeId: string | number | null | undefined
): string | null {
  if (scopeId == null || scopeId === "") {
    return null;
  }
  return String(scopeId);
}

function scopedSessionKey(scopeId: string): string {
  return `${SESSION_KEY_PREFIX}_${scopeId}`;
}

function scopedDismissKey(scopeId: string): string {
  return `${DISMISS_KEY_PREFIX}_${scopeId}`;
}

function removeLegacySessionKey(): void {
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function persistSetupChecklistSession(
  profileCompletionRequired: boolean,
  scopeId: string | number
): void {
  const scope = normalizeChecklistScopeId(scopeId);
  if (!scope) {
    return;
  }
  try {
    removeLegacySessionKey();
    sessionStorage.setItem(
      scopedSessionKey(scope),
      JSON.stringify({
        visible: true,
        profileCompletionRequired,
        scopeId: scope,
      } satisfies SetupChecklistSession)
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function readSetupChecklistSession(
  scopeId: string | number | null | undefined
): SetupChecklistSession | null {
  const scope = normalizeChecklistScopeId(scopeId);
  if (!scope) {
    return null;
  }
  try {
    removeLegacySessionKey();
    const raw = sessionStorage.getItem(scopedSessionKey(scope));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as SetupChecklistSession;
    if (parsed?.visible !== true || parsed.scopeId !== scope) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSetupChecklistSession(
  scopeId?: string | number | null
): void {
  try {
    const scope = normalizeChecklistScopeId(scopeId);
    if (scope) {
      sessionStorage.removeItem(scopedSessionKey(scope));
    }
    removeLegacySessionKey();
  } catch {
    /* ignore */
  }
}

export function isSetupChecklistDismissed(scopeId: string | number): boolean {
  const scope = normalizeChecklistScopeId(scopeId);
  if (!scope) {
    return false;
  }
  try {
    return localStorage.getItem(scopedDismissKey(scope)) === "1";
  } catch {
    return false;
  }
}

export function dismissSetupChecklist(scopeId: string | number): void {
  const scope = normalizeChecklistScopeId(scopeId);
  if (!scope) {
    return;
  }
  try {
    const session = readSetupChecklistSession(scope);
    if (session?.profileCompletionRequired) {
      return;
    }
    localStorage.setItem(scopedDismissKey(scope), "1");
    sessionStorage.removeItem(scopedSessionKey(scope));
  } catch {
    /* ignore */
  }
}

export type SetupChecklistRouterState = {
  showSetupChecklist: true;
  profileCompletionRequired: boolean;
};

/** Required (non-dismissible) checklist only when explicitly true — e.g. auto-step3 failure meta. */
export function isExplicitProfileCompletionRequired(value?: boolean): boolean {
  return value === true;
}

export type SetupChecklistHandoffInput =
  | {
      action: "navigate";
      profileCompletionRequired: boolean;
      scopeId?: string | number | null;
    }
  | {
      action: "ingest-router";
      routerState: {
        showSetupChecklist?: boolean;
        profileCompletionRequired?: boolean;
      };
      scopeId: string | number;
    };

export type SetupChecklistHandoffResult =
  | SetupChecklistRouterState
  | { visible: true; profileCompletionRequired: boolean }
  | null;

/**
 * Single checklist handoff API — used by guard, registration flow, and facilities page.
 */
export function applySetupChecklistHandoff(
  input: SetupChecklistHandoffInput
): SetupChecklistHandoffResult {
  if (input.action === "ingest-router") {
    if (
      !input.routerState.showSetupChecklist &&
      !input.routerState.profileCompletionRequired
    ) {
      return null;
    }
    const profileCompletionRequired = isExplicitProfileCompletionRequired(
      input.routerState.profileCompletionRequired
    );
    commitSetupChecklistHandoff(profileCompletionRequired, input.scopeId);
    return { visible: true, profileCompletionRequired };
  }

  const profileCompletionRequired = isExplicitProfileCompletionRequired(
    input.profileCompletionRequired
  );
  if (input.scopeId != null && input.scopeId !== "") {
    return commitSetupChecklistHandoff(profileCompletionRequired, input.scopeId);
  }
  return setupChecklistRouterState(profileCompletionRequired);
}

/** @deprecated Use {@link applySetupChecklistHandoff} with action `navigate`. */
export function buildSetupChecklistHandoff(profileCompletionRequired: boolean): {
  routerState: SetupChecklistRouterState;
  persistForScope: (scopeId: string | number) => void;
} {
  return {
    routerState: {
      showSetupChecklist: true,
      profileCompletionRequired,
    },
    persistForScope: (scopeId) => {
      persistSetupChecklistSession(profileCompletionRequired, scopeId);
    },
  };
}

/** Router state for navigate() — does not persist (use commitSetupChecklistHandoff when scopeId is known). */
export function setupChecklistRouterState(
  profileCompletionRequired: boolean
): SetupChecklistRouterState {
  return buildSetupChecklistHandoff(profileCompletionRequired).routerState;
}

/** Single handoff: persist session + return router state for navigate(). */
export function commitSetupChecklistHandoff(
  profileCompletionRequired: boolean,
  scopeId: string | number
): SetupChecklistRouterState {
  if (!profileCompletionRequired) {
    clearSetupChecklistSession(scopeId);
  }
  const handoff = buildSetupChecklistHandoff(profileCompletionRequired);
  handoff.persistForScope(scopeId);
  return handoff.routerState;
}

/** @deprecated Use {@link applySetupChecklistHandoff} with action `navigate`. */
export function prepareChecklistNavigateState(
  profileCompletionRequired: boolean,
  scopeId?: string | number | null
): SetupChecklistRouterState {
  const result = applySetupChecklistHandoff({
    action: "navigate",
    profileCompletionRequired,
    scopeId,
  });
  return result as SetupChecklistRouterState;
}

/** @deprecated Use {@link applySetupChecklistHandoff} with action `ingest-router`. */
export function ingestRouterChecklistHandoff(
  handoff: {
    showSetupChecklist?: boolean;
    profileCompletionRequired?: boolean;
  },
  scopeId: string | number
): { visible: true; profileCompletionRequired: boolean } | null {
  const result = applySetupChecklistHandoff({
    action: "ingest-router",
    routerState: handoff,
    scopeId,
  });
  return result as { visible: true; profileCompletionRequired: boolean } | null;
}

export function resolveInitialChecklistState(
  scopeId: string | number | null | undefined
): {
  visible: boolean;
  profileCompletionRequired: boolean;
} {
  const session = readSetupChecklistSession(scopeId);
  if (!session) {
    return { visible: false, profileCompletionRequired: false };
  }
  if (session.profileCompletionRequired) {
    return {
      visible: true,
      profileCompletionRequired: true,
    };
  }
  const scope = normalizeChecklistScopeId(scopeId);
  if (scope && isSetupChecklistDismissed(scope)) {
    return { visible: false, profileCompletionRequired: false };
  }
  return {
    visible: true,
    profileCompletionRequired: false,
  };
}

/** Remove scoped dismiss keys and legacy checklist storage on logout. */
export function clearSetupChecklistStorageOnLogout(): void {
  try {
    removeLegacySessionKey();
    localStorage.removeItem(SETUP_CHECKLIST_DISMISS_KEY);
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${DISMISS_KEY_PREFIX}_`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}
